// src/components/VaccineAdjustmentForm.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  Grid,
  GridItem,
  HStack,
  FormErrorMessage,
} from "@chakra-ui/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DataTable from "./AppDataTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSyringe,
  faTable,
  faTrash,
  faCalculator,
  faListOl,
  faGears,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import CustomDatePicker from "./CustomDatePicker";
import * as IconButton from "../themes/IconButtonSet";

// Custom styles for DataTable
const customStyles = {
  header: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "18px",
      fontWeight: "bold",
    },
  },
  headCells: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "16px",
      fontWeight: "semibold",
      padding: "10px",
    },
  },
  cells: {
    style: {
      padding: "10px",
      fontSize: "14px",
    },
  },
  rows: {
    style: {
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  },
};

const VaccineAdjustmentForm = () => {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const [formData, setFormData] = useState({
    TransactionID: transactionId || "ADJ-XXXX-XX-XXX",
    RequestDate: new Date(),
    WarehouseID: "",
    BudgetType: "",
    RequestType: "",
    ReferenceDocumentType: "",
    ReferenceDocumentNo: "",
    WarehouseStaffID: "",
    RequestDetail: "",
    RequesterID: profile?.Email || "",
    ApprovalStatus: "Pending",
    ApproverID: "",
    ApprovalDate: null,
    Remark: "",
    Status: "Pending",
    VaccineDetails: [],
  });

  const [warehouses, setWarehouses] = useState([]);
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [newDetail, setNewDetail] = useState({
    VaccineID: "",
    PreviousQuantity: "",
    RequestQuantity: "",
    LotID: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(!!transactionId);
  const [errors, setErrors] = useState({});

  const budgetTypeOptions = [
  { value: "Prevention", label: "งบป้องกัน" },
  { value: "Distribution", label: "งบจำหน่าย" }
];

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const fetchWarehouses = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouses(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
        return response.VaccinesList || [];
      }
      return [];
    } catch (error) {
      console.error("Error fetching vaccines:", error);
      return [];
    }
  };

  const fetchAdjustment = async (id, vaccines) => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineAdjustmentsByID",
        data: { TransactionID: id },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const adjustment = response.VaccineAdjustment;
        const details = response.VaccineDetails || [];
        const updatedDetails = details.map((detail) => {
          const vaccine = vaccines.find((v) => v.VaccineID === detail.VaccineID);
          return {
            VaccineID: detail.VaccineID,
            name: vaccine ? vaccine.VaccineNameTH : "ไม่พบชื่อวัคซีน",
            PreviousQuantity: detail.PreviousQuantity,
            RequestQuantity: detail.RequestQuantity,
            LotID: detail.LotID,
          };
        });
        setFormData({
          TransactionID: adjustment.TransactionID,
          RequestDate: new Date(adjustment.RequestDate),
          WarehouseID: adjustment.WarehouseID,
          BudgetType: adjustment.BudgetType,
          RequestType: adjustment.RequestType,
          ReferenceDocumentType: adjustment.ReferenceDocumentType,
          ReferenceDocumentNo: adjustment.ReferenceDocumentNo,
          WarehouseStaffID: adjustment.WarehouseStaffID,
          RequestDetail: adjustment.RequestDetail,
          RequesterID: adjustment.RequesterID,
          ApprovalStatus: adjustment.ApprovalStatus,
          ApproverID: adjustment.ApproverID || "",
          ApprovalDate: adjustment.ApprovalDate ? new Date(adjustment.ApprovalDate) : null,
          Remark: adjustment.Remark,
          Status: adjustment.Status,
          VaccineDetails: updatedDetails,
        });
      }
    } catch (error) {
      console.error("Error fetching adjustment:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (sessionId) {
        await fetchWarehouses();
        const vaccines = await fetchVaccineOptions();
        if (transactionId) {
          await fetchAdjustment(transactionId, vaccines);
        }
      }
    };
    loadData();
  }, [sessionId, transactionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleDateChange = (date, name) => {
    setFormData({ ...formData, [name]: date });
  };

  const handleDetailInputChange = (e) => {
    const { name, value } = e.target;
    setNewDetail({ ...newDetail, [name]: value });
  };

  const handleDetailQuantityChange = (vaccineId, field, value) => {
    const newValue = value === "" ? 0 : parseInt(value, 10);
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId) {
        return { ...detail, [field]: newValue };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "WarehouseID":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return formData.WarehouseID && Object.keys(errors).length === 0;
  };

  const handleAddDetail = () => {
    if (newDetail.VaccineID && newDetail.RequestQuantity !== "" && newDetail.LotID) {
      const selectedVaccine = vaccineOptions.find((v) => v.VaccineID === newDetail.VaccineID);
      if (selectedVaccine) {
        const detailData = {
          VaccineID: newDetail.VaccineID,
          name: selectedVaccine.VaccineNameTH,
          PreviousQuantity: parseInt(newDetail.PreviousQuantity) || 0,
          RequestQuantity: parseInt(newDetail.RequestQuantity),
          LotID: newDetail.LotID,
        };
        setFormData((prevFormData) => ({
          ...prevFormData,
          VaccineDetails: [...prevFormData.VaccineDetails, detailData],
        }));
        setNewDetail({ VaccineID: "", PreviousQuantity: "", RequestQuantity: "", LotID: "" });
      } else {
        console.warn(`VaccineID ${newDetail.VaccineID} not found in vaccineOptions`);
      }
    } else {
      console.warn("Missing required fields:", newDetail);
    }
  };

  const handleRemoveDetail = (row) => {
    setFormData({
      ...formData,
      VaccineDetails: formData.VaccineDetails.filter(
        (v) => v.VaccineID !== row.VaccineID || v.LotID !== row.LotID
      ),
    });
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/createVaccineAdjustments",
        data: { ...formData, VaccineDetails: [] },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setFormData({ ...formData, TransactionID: response.TransactionID });
        setIsSubmitted(true);
        alert("สร้างเอกสารปรับยอดสำเร็จ! เลขที่เอกสาร: " + response.TransactionID);
      }
    } catch (error) {
      console.error("Error creating adjustment:", error);
      alert("เกิดข้อผิดพลาดในการสร้างเอกสารปรับยอด: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    const date = formData.RequestDate;
    const formattedRequestDate = date
      ? `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
          .getDate()
          .toString()
          .padStart(2, "0")}/${date.getFullYear()}`
      : "";
    const approvalDate = formData.ApprovalDate;
    const formattedApprovalDate = approvalDate
      ? `${(approvalDate.getMonth() + 1).toString().padStart(2, "0")}/${approvalDate
          .getDate()
          .toString()
          .padStart(2, "0")}/${approvalDate.getFullYear()}`
      : null;

    const submitData = {
      ...formData,
      RequestDate: formattedRequestDate,
      ApprovalDate: formattedApprovalDate,
      VaccineDetails: formData.VaccineDetails.map((detail) => ({
        ...detail,
      })),
    };

    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateVaccineAdjustmentsByID",
        data: submitData,
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert("บันทึกใบปรับยอดสำเร็จ!");
        navigate("/vaccineadjustmentlist");
      }
    } catch (error) {
      console.error("API Error:", error.message);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const isEditable = !isSubmitted;

  const columns = [
    {
      name: <HStack><FontAwesomeIcon icon={faListOl} /><span>รหัสวัคซีน</span></HStack>,
      selector: (row) => row.VaccineID,
      sortable: true,
      width: "150px",
    },
    {
      name: <HStack><FontAwesomeIcon icon={faSyringe} /><span>ชื่อวัคซีน</span></HStack>,
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: <HStack><FontAwesomeIcon icon={faCalculator} /><span>Lot No.</span></HStack>,
      selector: (row) => row.LotID,
      sortable: true,
      width: "150px",
    },
    {
      name: <HStack><FontAwesomeIcon icon={faCalculator} /><span>ยอดก่อนปรับ (ขวด)</span></HStack>,
      selector: (row) => row.PreviousQuantity,
      sortable: true,
      cell: (row) => (
        <Input
          type="number"
          value={row.PreviousQuantity}
          onChange={(e) => handleDetailQuantityChange(row.VaccineID, "PreviousQuantity", e.target.value)}
          min={0}
          size="sm"
          width="100px"
          ml={10}
        />
      ),
      width: "200px",
    },
    {
      name: <HStack><FontAwesomeIcon icon={faCalculator} /><span>ยอดที่ขอปรับ (ขวด)</span></HStack>,
      selector: (row) => row.RequestQuantity,
      sortable: true,
      cell: (row) => (
        <Input
          type="number"
          value={row.RequestQuantity}
          onChange={(e) => handleDetailQuantityChange(row.VaccineID, "RequestQuantity", e.target.value)}
          min={0}
          size="sm"
          width="100px"
          ml={10}
        />
      ),
      width: "200px",
    },
    {
      name: <HStack><FontAwesomeIcon icon={faGears} /><span>การดำเนินการ</span></HStack>,
      cell: (row) => (
        <IconButton.DeleteIcon onClick={() => handleRemoveDetail(row)} size="sm" />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "150px",
    },
  ];

  if (!sessionId) {
    return null;
  }

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <Heading as="h2" mb={10} textAlign="center">
        รายการปรับยอดวัคซีนคงคลัง
      </Heading>

      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading size="lg" fontWeight="normal">
              ข้อมูลใบปรับยอดวัคซีน
            </Heading>
          </HStack>
        </Box>
        <Grid templateColumns="repeat(4, 1fr)" gap={6} p={5}>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">เลขที่เอกสารปรับยอด</FormLabel>
              <Input
                name="TransactionID"
                value={formData.TransactionID}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">วันที่ขอปรับยอด</FormLabel>
              <CustomDatePicker
                name="RequestDate"
                value={formData.RequestDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={2}>
            <FormControl isRequired isInvalid={!!errors.WarehouseID} isDisabled={isSubmitted}>
              <FormLabel fontSize="xl" color="gray">คลัง</FormLabel>
              <Select
                name="WarehouseID"
                value={formData.WarehouseID}
                onChange={handleInputChange}
                placeholder="เลือกคลัง"
                colorScheme="iBlue"
              >
                {warehouses.map((warehouse) => (
                  <option key={warehouse.WarehouseID} value={warehouse.WarehouseID}>
                    {warehouse.WarehouseName}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.WarehouseID}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">ประเภทงบประมาณ</FormLabel>
              <Select
                name="BudgetType"
                value={formData.BudgetType}
                onChange={handleInputChange}
                placeholder="เลือกประเภทงบประมาณ"
                colorScheme="iBlue"
              >
                {budgetTypeOptions.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">ประเภทการขอปรับยอด</FormLabel>
              <Input
                name="RequestType"
                value={formData.RequestType}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">ประเภทเอกสารอ้างอิง</FormLabel>
              <Input
                name="ReferenceDocumentType"
                value={formData.ReferenceDocumentType}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">เลขที่เอกสารอ้างอิง</FormLabel>
              <Input
                name="ReferenceDocumentNo"
                value={formData.ReferenceDocumentNo}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">ผู้ขอปรับ</FormLabel>
              <Input
                name="RequesterID"
                value={formData.RequesterID}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">เจ้าหน้าที่คลัง</FormLabel>
              <Input
                name="WarehouseStaffID"
                value={formData.WarehouseStaffID}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={4}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">รายละเอียดการขอปรับ</FormLabel>
              <Textarea
                name="RequestDetail"
                value={formData.RequestDetail}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={4}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">หมายเหตุ</FormLabel>
              <Textarea
                name="Remark"
                value={formData.Remark}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">สถานะ</FormLabel>
              <Input
                name="Status"
                value={formData.Status}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">สถานะการอนุมัติ</FormLabel>
              <Input
                name="ApprovalStatus"
                value={formData.ApprovalStatus}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">ผู้อนุมัติ</FormLabel>
              <Input
                name="ApproverID"
                value={formData.ApproverID}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">วันที่อนุมัติ</FormLabel>
              <CustomDatePicker
                value={formData.ApprovalDate}
                onChange={(date) => handleDateChange(date, "ApprovalDate")}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
        </Grid>
        {!isSubmitted && (
          <Box p={5} textAlign="right">
            <HStack spacing={5} mb={5} textAlign="right">
              <IconButton.Cancel onClick={() => navigate("/vaccineadjustmentlist")} />
              <IconButton.Submit label="สร้างเอกสาร" onClick={handleSubmit} />
            </HStack>
          </Box>
        )}
      </Box>

      {isSubmitted && (
        <>
          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
            <Box
              mb={5}
              borderRadius="full"
              w="100%"
              bgColor="iPurple.500"
              color="white"
              p={2}
            >
              <HStack spacing={5} ml={5}>
                <FontAwesomeIcon icon={faSyringe} color="white" size="xl" />
                <Heading size="lg" fontWeight="normal">
                  เพิ่มรายการวัคซีนที่ขอปรับยอด
                </Heading>
              </HStack>
            </Box>
            <Grid templateColumns="repeat(5, 1fr)" gap={6} p={5}>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">วัคซีน</FormLabel>
                <Select
                  name="VaccineID"
                  value={newDetail.VaccineID}
                  onChange={handleDetailInputChange}
                  placeholder="เลือกวัคซีน"
                  colorScheme="iPurple"
                >
                  {vaccineOptions.map((vaccine) => (
                    <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                      {vaccine.VaccineNameTH}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">Lot No.</FormLabel>
                <Input
                  name="LotID"
                  value={newDetail.LotID}
                  onChange={handleDetailInputChange}
                  colorScheme="iPurple"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">ยอดก่อนปรับ (ขวด)</FormLabel>
                <Input
                  name="PreviousQuantity"
                  type="number"
                  value={newDetail.PreviousQuantity}
                  onChange={handleDetailInputChange}
                  min={0}
                  colorScheme="iPurple"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">ยอดที่ขอปรับ (ขวด)</FormLabel>
                <Input
                  name="RequestQuantity"
                  type="number"
                  value={newDetail.RequestQuantity}
                  onChange={handleDetailInputChange}
                  min={0}
                  colorScheme="iPurple"
                />
              </FormControl>
              <Box mt={10}>
                <IconButton.Add onClick={handleAddDetail} h={9} />
              </Box>
            </Grid>
          </Box>

          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
            <Box
              mb={5}
              borderRadius="full"
              w="100%"
              bgColor="iOrange.500"
              color="white"
              p={2}
            >
              <HStack spacing={5} ml={5}>
                <FontAwesomeIcon icon={faTable} color="white" size="xl" />
                <Heading size={{ base: "md", md: "lg" }} fontWeight="normal">
                  รายการวัคซีนที่ขอปรับยอด
                </Heading>
              </HStack>
            </Box>
            <Box p={5} overflowX="auto">
              <DataTable
                columns={columns}
                data={formData.VaccineDetails}
                pagination
                highlightOnHover
                striped
                responsive
                customStyles={customStyles}
              />
            </Box>
          </Box>

          <Box p={5} textAlign="right">
            <HStack spacing={5} mb={5}>
              <IconButton.Back
                onClick={() => navigate("/vaccineadjustmentlist")}
                label="ย้อนกลับ"
              />
              <IconButton.Save onClick={handleConfirm} isLoading={isLoading} />
            </HStack>
          </Box>
        </>
      )}
    </Box>
  );
};

export default VaccineAdjustmentForm;